import React, { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  TextField,
  IconButton,
  Chip,
  MenuItem,
} from "@mui/material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { ChevronUp, ChevronDown, Trash2, MapPin, CalendarClock } from "lucide-react";
import { useTheme } from "../../../../../context/ThemeContext";
import { STEP_TYPES, STEP_CONFIG } from "./transportConstants";

/* ── Bus companies ─────────────────────────────────────────────────── */
// Voi siirtää transportConstants.js:ään jos halutaan jakaa muualle

const BUS_COMPANIES = [
  "OnniBus",
  "Kosonen",
  "Soisalo",
  "Savonlinja",
  "Koivisto",
  "Pekola",
];

const OTHER_COMPANY = "__other__";

const DEFAULT_COMPANY = "OnniBus";

const BusDetailsFields = ({ step, onChange }) => {
  const value = step.busCompany;

  // Jos arvo on jo jokin muu kuin listalla oleva (esim. tallennettu data),
  // näytetään "Muu" + tekstikenttä valmiiksi
  const [isOther, setIsOther] = useState(
    () => value !== "" && !BUS_COMPANIES.includes(value)
  );

  // Uusi vaihe ilman arvoa → valitaan OnniBus oletuksena (myös dataan)
  React.useEffect(() => {
    if (value === "" && !isOther) {
      onChange("busCompany", DEFAULT_COMPANY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectValue = isOther
    ? OTHER_COMPANY
    : BUS_COMPANIES.includes(value)
      ? value
      : "";

  const handleSelect = (e) => {
    const v = e.target.value;
    if (v === OTHER_COMPANY) {
      setIsOther(true);
      onChange("busCompany", ""); // tyhjennetään, käyttäjä kirjoittaa nimen itse
    } else {
      setIsOther(false);
      onChange("busCompany", v);
    }
  };

  return (
    <Stack spacing={1.5}>
      {/* Row 1: yhtiö (leveä) + linja (kapea) */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <TextField
          label="Bussiyhtiö"
          size="small"
          select
          value={selectValue}
          onChange={handleSelect}
          fullWidth
          sx={{
            flex: { sm: 2 },
            "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
          }}
        >
          {BUS_COMPANIES.map((company) => (
            <MenuItem key={company} value={company}>
              {company}
            </MenuItem>
          ))}
          <MenuItem value={OTHER_COMPANY}>Muu</MenuItem>
        </TextField>

        <TextField
          label="Bussilinja"
          size="small"
          value={step.busLine}
          onChange={(e) => onChange("busLine", e.target.value)}
          fullWidth
          placeholder="Esim. OB5"
          sx={{
            flex: { sm: 1 },
            minWidth: { sm: 110 },
            "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
          }}
        />
      </Stack>

      {/* Row 2: oma yhtiön nimi, vain kun "Muu" valittu */}
      {isOther && (
        <TextField
          label="Bussiyhtiön nimi"
          size="small"
          value={value}
          onChange={(e) => onChange("busCompany", e.target.value)}
          fullWidth
          autoFocus
          placeholder="Esim. Matkahuolto"
          helperText="Kirjoita bussiyhtiön nimi"
          FormHelperTextProps={{ sx: { ml: 0.5 } }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
        />
      )}
    </Stack>
  );
};

/* ── Wait step: locked display ─────────────────────────────────────── */

const WaitStepFields = ({ step, onChange }) => {
  const muiTheme = useMuiTheme();

  return (
    <Stack spacing={1.5}>
      <TextField
        label="Vaihtoasema"
        size="small"
        value={step.departure}
        disabled
        fullWidth
        InputProps={{
          startAdornment: (
            <MapPin size={14} color={muiTheme.palette.text.disabled}
              style={{ marginRight: 6, flexShrink: 0 }} />
          ),
        }}
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
      />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <TextField
          label="Vaihto alkaa"
          type="datetime-local"
          size="small"
          value={step.departureTime}
          disabled
          fullWidth
          InputLabelProps={{ shrink: true }}
          sx={{
            "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
            "& input": { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
          }}
        />
        <TextField
          label="Vaihto päättyy"
          type="datetime-local"
          size="small"
          value={step.arrivalTime}
          onChange={(e) => onChange("arrivalTime", e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <CalendarClock size={14} color={muiTheme.palette.info.main}
                style={{ marginRight: 6, flexShrink: 0 }} />
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
            "& input": { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
          }}
        />
      </Stack>
    </Stack>
  );
};

/* ── Bus / Car step fields ─────────────────────────────────────────── */

const TransportStepFields = ({ step, onChange }) => {
  const muiTheme = useMuiTheme();

  return (
    <Stack spacing={1.5}>
      {/* Locations */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <TextField
          label="Lähtöpaikka"
          size="small"
          value={step.departure}
          onChange={(e) => onChange("departure", e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <MapPin size={14} color={muiTheme.palette.success.main}
                style={{ marginRight: 6, flexShrink: 0 }} />
            ),
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
        />
        <TextField
          label="Määränpää"
          size="small"
          value={step.arrival}
          onChange={(e) => onChange("arrival", e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <MapPin size={14} color={muiTheme.palette.error.main}
                style={{ marginRight: 6, flexShrink: 0 }} />
            ),
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
        />
      </Stack>

      {/* Times */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <TextField
          label="Lähtöaika"
          type="datetime-local"
          size="small"
          value={step.departureTime}
          onChange={(e) => onChange("departureTime", e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <CalendarClock size={14} color={muiTheme.palette.success.main}
                style={{ marginRight: 6, flexShrink: 0 }} />
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
            "& input": { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
          }}
        />
        <TextField
          label="Saapumisaika"
          type="datetime-local"
          size="small"
          value={step.arrivalTime}
          onChange={(e) => onChange("arrivalTime", e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <CalendarClock size={14} color={muiTheme.palette.error.main}
                style={{ marginRight: 6, flexShrink: 0 }} />
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
            "& input": { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
          }}
        />
      </Stack>

      {/* Bus-only: company + line */}
      {step.type === STEP_TYPES.BUS && (
        <BusDetailsFields step={step} onChange={onChange} />
      )}
    </Stack>
  );
};

/* ── StepEditor ────────────────────────────────────────────────────── */

export const StepEditor = ({
  step,
  index,
  isLast,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  totalSteps,
}) => {
  const muiTheme = useMuiTheme();
  const { isDark } = useTheme();
  const config = STEP_CONFIG[step.type];
  const Icon = config.icon;

  const handleFieldChange = (field, value) => {
    onChange(index, { ...step, [field]: value });
  };

  const isWait = step.type === STEP_TYPES.WAIT;

  return (
    <Stack direction="row" spacing={1.5} sx={{ position: "relative" }}>
      {/* Timeline rail */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: 26,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            bgcolor: isDark ? "rgba(255,255,255,0.08)" : "grey.100",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={14} color={muiTheme.palette[config.color].main} />
        </Box>
        {!isLast && (
          <Box sx={{ width: 2, flex: 1, bgcolor: "divider", my: 0.5 }} />
        )}
      </Box>

      {/* Step content */}
      <Box sx={{ flex: 1, pb: isLast ? 0 : 2 }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={config.label}
              size="small"
              color={config.color}
              sx={{ fontWeight: 700, fontSize: "0.75rem" }}
            />
            <Typography variant="caption" color="text.secondary">
              Vaihe {index + 1}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={0.25}>
            {!isWait && index > 0 && (
              <IconButton size="small" onClick={() => onMoveUp(index)} sx={{ p: 0.5 }}>
                <ChevronUp size={14} />
              </IconButton>
            )}
            {!isWait && index < totalSteps - 1 && (
              <IconButton size="small" onClick={() => onMoveDown(index)} sx={{ p: 0.5 }}>
                <ChevronDown size={14} />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={() => onRemove(index)}
              sx={{ p: 0.5, color: "error.main" }}
            >
              <Trash2 size={14} />
            </IconButton>
          </Stack>
        </Stack>

        {/* Fields — locked for wait, editable for bus/car */}
        {isWait ? (
          <WaitStepFields step={step} onChange={handleFieldChange} />
        ) : (
          <TransportStepFields step={step} onChange={handleFieldChange} />
        )}
      </Box>
    </Stack>
  );
};